/**
 * v3.ira.disclosureBodies — WAVE-12 IRA Wave 5.5 client.
 *
 * Backed by Functions/IraDisclosureBodiesFunction.cs:
 *   GET    /v3/ira/disclosure-bodies                List templates
 *   GET    /v3/ira/disclosure-bodies/{code}         Get one
 *   PUT    /v3/ira/disclosure-bodies/{code}         Save body (admin)
 *   GET    /v3/ira/workflow-disclosure-links        List linkage rows
 *   PUT    /v3/ira/workflow-disclosure-links        Upsert (admin)
 *   DELETE /v3/ira/workflow-disclosure-links        Deactivate (admin)
 */

import type {
  RequestOptions,
  DisclosureTemplateBodyDto,
  DisclosureTemplateBodyListResponse,
  SaveDisclosureBodyInput,
  SaveDisclosureBodyResponse,
  UpsertWorkflowDisclosureLinkInput,
  WorkflowDisclosureLinkDto,
  WorkflowDisclosureLinkListResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

export interface DisclosureTemplateBodyGetResponse {
  success: boolean;
  template?: DisclosureTemplateBodyDto;
  error?: string;
}

export interface UpsertWorkflowDisclosureLinkResponse {
  success: boolean;
  link?: Pick<
    WorkflowDisclosureLinkDto,
    'workflowCode' | 'stepCode' | 'templateCode' | 'isMandatory' | 'sortOrder'
  > & { isActive?: boolean; conditionExpression?: string };
  error?: string;
}

export class IraDisclosureBodiesApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(
    filters: { layer?: string; includeInactive?: boolean } = {},
    opts?: RequestOptions,
  ): Promise<DisclosureTemplateBodyListResponse> {
    return this.http.get<DisclosureTemplateBodyListResponse>('/v3/ira/disclosure-bodies', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public get(code: string, opts?: RequestOptions): Promise<DisclosureTemplateBodyGetResponse> {
    return this.http.get<DisclosureTemplateBodyGetResponse>(
      `/v3/ira/disclosure-bodies/${encodeURIComponent(code)}`,
      { ...opts },
    );
  }

  public saveBody(
    bypassKey: string,
    code: string,
    body: SaveDisclosureBodyInput,
    opts?: RequestOptions,
  ): Promise<SaveDisclosureBodyResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.put<SaveDisclosureBodyResponse>(
      `/v3/ira/disclosure-bodies/${encodeURIComponent(code)}`,
      { ...opts, headers, body },
    );
  }

  public listLinks(
    filters: { workflowCode?: string; stepCode?: string; includeInactive?: boolean } = {},
    opts?: RequestOptions,
  ): Promise<WorkflowDisclosureLinkListResponse> {
    return this.http.get<WorkflowDisclosureLinkListResponse>(
      '/v3/ira/workflow-disclosure-links',
      {
        ...opts,
        query: filters as unknown as Record<string, string | number | boolean | undefined>,
      },
    );
  }

  public upsertLink(
    bypassKey: string,
    body: UpsertWorkflowDisclosureLinkInput,
    opts?: RequestOptions,
  ): Promise<UpsertWorkflowDisclosureLinkResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.put<UpsertWorkflowDisclosureLinkResponse>(
      '/v3/ira/workflow-disclosure-links',
      { ...opts, headers, body },
    );
  }

  public deactivateLink(
    bypassKey: string,
    workflowCode: string,
    stepCode: string,
    templateCode: string,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; error?: string }> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<{ success: boolean; error?: string }>(
      '/v3/ira/workflow-disclosure-links',
      {
        ...opts,
        headers,
        query: { workflowCode, stepCode, templateCode },
      },
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Module augmentation — extend IraApi with `.disclosureBodies`.
// ─────────────────────────────────────────────────────────────────────────

declare module './ira.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface IraApi {
    /** WAVE-12 IRA Wave 5.5 — inline disclosure bodies + workflow linkage. */
    readonly disclosureBodies: IraDisclosureBodiesApi;
  }
}
